import { FC, useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@capacit-com/lara-law-shared-ui";
import PageContent from "@/pages/PageContent";
import useTenant from "@/hooks/useTenant";
import { useServices } from "@/hooks/useServices";
import { logger } from "@/lib/logger";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import {
  EntraDirectorySyncTenantResult,
  EntraDirectoryUserChangeType,
} from "@/models/entraDirectorySync/EntraDirectorySync";
import { restError } from "@/models/client/RestResponse";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString("da-DK") : "—";

const changeGroupOrder: EntraDirectoryUserChangeType[] = [
  "RoleUpdated",
  "Removed",
  "RemovedInvitation",
  "RemovalBlocked",
  "Restored",
  "NewInEntraOnly",
];

const changeGroupLabels: Record<EntraDirectoryUserChangeType, string> = {
  RoleUpdated: "Roller opdateres",
  Removed: "Fjernes",
  RemovedInvitation: "Invitationer fjernes (aldrig logget ind)",
  RemovalBlocked: "Fjernelser blokeret af værnet",
  Restored: "Gendannes",
  NewInEntraOnly: "Nye i EA (venter på login)",
};

// The last live-run report arrives as a JSON string on the status; validate before use.
const userChangeSchema = z.object({
  email: z.string(),
  change: z.enum([
    "RoleUpdated",
    "Removed",
    "RemovedInvitation",
    "RemovalBlocked",
    "Restored",
    "NewInEntraOnly",
  ]),
  fromRole: z.string().nullish(),
  toRole: z.string().nullish(),
});

const syncResultSchema = z.object({
  tenantId: z.string(),
  dryRun: z.boolean(),
  rolesUpdated: z.number(),
  usersRemoved: z.number(),
  usersRestored: z.number(),
  skippedNewInEntraOnly: z.number(),
  removalsBlocked: z.number().default(0),
  // Reports stored before the directory-wide mode existed came from strict EAs.
  assignmentRequired: z.boolean().default(true),
  directoryUserCount: z.number().nullish(),
  keptByDirectoryMembership: z.number().default(0),
  errors: z.array(z.string()).default([]),
  warnings: z.array(z.string()).default([]),
  userChanges: z.array(userChangeSchema).default([]),
});

const parseStoredReport = (
  json?: string
): EntraDirectorySyncTenantResult | null => {
  if (!json) return null;
  try {
    const parsed = syncResultSchema.safeParse(JSON.parse(json));
    if (!parsed.success) {
      logger.error("Stored Entra sync report has an unexpected shape", {
        operation: "parseEntraDirectorySyncReport",
      });
      return null;
    }
    return parsed.data;
  } catch (error) {
    logger.error("Stored Entra sync report is not valid JSON", {
      operation: "parseEntraDirectorySyncReport",
      error,
    });
    return null;
  }
};

const counterRows = (
  result: EntraDirectorySyncTenantResult
): { label: string; value: number }[] => [
  { label: "Roller opdateret", value: result.rolesUpdated },
  { label: "Brugere fjernet", value: result.usersRemoved },
  { label: "Brugere gendannet", value: result.usersRestored },
  { label: "Nye i EA (venter på login)", value: result.skippedNewInEntraOnly },
  ...(result.removalsBlocked > 0
    ? [{ label: "Fjernelser blokeret", value: result.removalsBlocked }]
    : []),
  ...(result.assignmentRequired
    ? []
    : [
        {
          label: "Beholdt via directory-medlemskab",
          value: result.keptByDirectoryMembership,
        },
      ]),
];

const SyncResultReport: FC<{
  title: string;
  subtitle?: string;
  result: EntraDirectorySyncTenantResult;
}> = ({ title, subtitle, result }) => (
  <Card className="shrink-0">
    <CardHeader>
      <CardTitle className="text-base">{title}</CardTitle>
      {subtitle && <CardDescription>{subtitle}</CardDescription>}
    </CardHeader>
    <CardContent className="flex flex-col gap-4 text-sm">
      {!result.assignmentRequired && (
        <p className="rounded-md border bg-muted px-3 py-2 text-muted-foreground">
          Enterprise Application har <strong>Assignment required = Nej</strong>:
          alle {result.directoryUserCount ?? "?"} aktive brugere i kundens
          Entra-directory kan logge ind og beholdes i LODA. Kun brugere, der er
          fjernet eller deaktiveret i directoryet, fjernes. Roller styres stadig
          kun af de eksplicitte tildelinger under &quot;Brugere og
          grupper&quot;.
        </p>
      )}
      <dl className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {counterRows(result).map(({ label, value }) => (
          <div key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-lg font-medium">{value}</dd>
          </div>
        ))}
      </dl>

      {changeGroupOrder.map((type) => {
        const entries = (result.userChanges ?? []).filter(
          (change) => change.change === type
        );
        if (entries.length === 0) return null;
        return (
          <details key={type} className="rounded-md border px-3 py-2">
            <summary className="cursor-pointer font-medium">
              {changeGroupLabels[type]} ({entries.length})
            </summary>
            <ul className="mt-2 max-h-72 list-disc overflow-y-auto pl-5">
              {entries.map((entry) => (
                <li key={`${type}-${entry.email}`}>
                  {entry.email}
                  {type === "RoleUpdated" && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({entry.fromRole ?? "ingen rolle"} → {entry.toRole})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        );
      })}

      {result.warnings.length > 0 && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
          <p className="font-medium text-warning">Advarsler</p>
          <ul className="mt-1 list-disc pl-5 text-warning">
            {result.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {result.errors.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2">
          <p className="font-medium text-destructive">Fejl</p>
          <ul className="mt-1 list-disc pl-5 text-destructive">
            {result.errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </CardContent>
  </Card>
);

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Ukendt fejl";

const EntraDirectorySyncView: FC = () => {
  const { useQueryCurrent } = useTenant();
  const tenantQuery = useQueryCurrent();
  const tenantId = tenantQuery.data?.id;
  const { items } = useServices();
  const queryClient = useQueryClient();
  const [dryRunResult, setDryRunResult] =
    useState<EntraDirectorySyncTenantResult | null>(null);
  // After starting a live run, keep polling for a while even if the first status read
  // predates the in-progress stamp or the run already finished.
  const [pollUntil, setPollUntil] = useState<number | null>(null);

  const invalidateStatus = useCallback(() => {
    if (tenantId) {
      void queryClient.invalidateQueries({
        queryKey: ["entra-directory-sync", "status", tenantId],
      });
    }
  }, [queryClient, tenantId]);

  const statusQuery = useQuery({
    queryKey: ["entra-directory-sync", "status", tenantId],
    queryFn: async () => {
      if (!tenantId) throw new Error("Missing tenant id");
      const response = await items.entraDirectorySync.getStatus(tenantId);
      if (!response.success) {
        logger.error("Failed to load Entra sync status", {
          operation: "getEntraDirectorySyncStatus",
          tenantId,
          status: response.status,
        });
        throw restError(response);
      }
      return response.value;
    },
    enabled: Boolean(tenantId),
    // A background live run is in flight while liveRunStartedAt is set — keep polling,
    // and for a grace window right after starting one.
    refetchInterval: (query: {
      state: { data?: { liveRunStartedAt?: string } };
    }) =>
      query.state.data?.liveRunStartedAt ||
      (pollUntil !== null && Date.now() < pollUntil)
        ? 3000
        : false,
  });

  const status = statusQuery.data;
  const liveRunInProgress = Boolean(status?.liveRunStartedAt);
  // The API persists every dry-run delta, so the reviewed list, its warnings and the
  // override stay available across a refresh instead of only living in component state.
  const persistedDryRun = useMemo(
    () => parseStoredReport(status?.lastDryRunReportJson),
    [status?.lastDryRunReportJson]
  );
  const dryRunReport = dryRunResult ?? persistedDryRun;

  const lastLiveReport = useMemo(
    () => parseStoredReport(status?.lastLiveRunReportJson),
    [status?.lastLiveRunReportJson]
  );

  const consentUrlMutation = useMutation({
    mutationKey: ["entra-directory-sync", "consent-url"],
    mutationFn: async () => {
      if (!tenantId) throw new Error("Missing tenant id");
      const response = await items.entraDirectorySync.getConsentUrl(tenantId);
      if (!response.success || !response.value?.consentUrl) {
        logger.error("Failed to get Entra consent URL", {
          operation: "getEntraDirectorySyncConsentUrl",
          tenantId,
          status: response.status,
        });
        throw new Error("Kunne ikke hente consent-link");
      }
      window.open(response.value.consentUrl, "_blank", "noopener,noreferrer");
    },
    onError: (error) => showErrorToast(errorMessage(error)),
  });

  const dryRunMutation = useMutation({
    mutationKey: ["entra-directory-sync", "dry-run"],
    mutationFn: async () => {
      if (!tenantId) throw new Error("Missing tenant id");
      const response = await items.entraDirectorySync.runDryRun(tenantId);
      if (!response.success) {
        logger.error("Entra directory sync dry-run failed", {
          operation: "runEntraDirectorySyncDryRun",
          tenantId,
          status: response.status,
        });
        throw restError(response);
      }
      return response.value;
    },
    onSuccess: (data) => {
      setDryRunResult(data);
      showSuccessToast("Dry-run gennemført — gennemgå resultatet nedenfor.");
      invalidateStatus();
    },
    onError: (error) => showErrorToast(errorMessage(error)),
  });

  const approveLiveMutation = useMutation({
    mutationKey: ["entra-directory-sync", "approve-live"],
    mutationFn: async () => {
      if (!tenantId) throw new Error("Missing tenant id");
      const response = await items.entraDirectorySync.approveLive(tenantId);
      if (!response.success) {
        logger.error("Failed to approve live Entra sync", {
          operation: "approveEntraDirectorySyncLive",
          tenantId,
          status: response.status,
        });
        throw restError(response);
      }
    },
    onSuccess: () => {
      showSuccessToast("Live sync godkendt.");
      invalidateStatus();
    },
    onError: (error) => showErrorToast(errorMessage(error)),
  });

  const disableLiveMutation = useMutation({
    mutationKey: ["entra-directory-sync", "disable-live"],
    mutationFn: async () => {
      if (!tenantId) throw new Error("Missing tenant id");
      const response = await items.entraDirectorySync.disableLive(tenantId);
      if (!response.success) {
        logger.error("Failed to disable live Entra sync", {
          operation: "disableEntraDirectorySyncLive",
          tenantId,
          status: response.status,
        });
        throw restError(response);
      }
    },
    onSuccess: () => {
      showSuccessToast("Live sync deaktiveret.");
      invalidateStatus();
    },
    onError: (error) => showErrorToast(errorMessage(error)),
  });

  const runLiveMutation = useMutation({
    mutationKey: ["entra-directory-sync", "run-live"],
    mutationFn: async (expectedRemovals: number | null) => {
      if (!tenantId) throw new Error("Missing tenant id");
      const response = await items.entraDirectorySync.runLive(
        tenantId,
        expectedRemovals
      );
      if (!response.success) {
        logger.error("Failed to start live Entra sync run", {
          operation: "runEntraDirectorySyncLive",
          tenantId,
          status: response.status,
        });
        throw restError(response);
      }
    },
    onSuccess: () => {
      showSuccessToast(
        "Live-kørsel startet. Resultatet vises her under 'Seneste live-kørsel', når den er færdig.",
        "Startet"
      );
      setPollUntil(Date.now() + 30_000);
      invalidateStatus();
    },
    onError: (error) => showErrorToast(errorMessage(error)),
  });

  const handleApproveLive = useCallback(() => {
    if (
      !window.confirm(
        "Godkend live sync? Brugere der fjernes fra Enterprise Application vil blive soft-slettet i LODA ved næste job-kørsel."
      )
    ) {
      return;
    }
    approveLiveMutation.mutate();
  }, [approveLiveMutation]);

  const handleDisableLive = useCallback(() => {
    if (
      !window.confirm(
        "Deaktiver live sync? Den timevise synkronisering går tilbage til dry-run-tilstand og ændrer ikke brugere."
      )
    ) {
      return;
    }
    disableLiveMutation.mutate();
  }, [disableLiveMutation]);

  const handleRunLive = useCallback(() => {
    if (
      !window.confirm(
        "Kør live sync nu? Værnet mod massefjernelser er aktivt: fjernelser over grænsen blokeres, mens roller opdateres og brugere gendannes."
      )
    ) {
      return;
    }
    runLiveMutation.mutate(null);
  }, [runLiveMutation]);

  const handleOverrideGuard = useCallback(() => {
    const reviewed = dryRunReport?.usersRemoved ?? 0;
    const answer = window.prompt(
      `TILSIDESÆT VÆRNET: ${reviewed} bruger(e) fra den seneste dry-run bliver soft-slettet i LODA.\n\n` +
        `Skriv tallet ${reviewed} for at bekræfte, at du har gennemgået listen. Annullér for at fortryde.`,
      ""
    );
    if (answer === null) return;
    if (answer.trim() !== String(reviewed)) {
      showErrorToast(
        "Tallet matcher ikke den seneste dry-run — ingen kørsel startet."
      );
      return;
    }
    runLiveMutation.mutate(reviewed);
  }, [dryRunReport, runLiveMutation]);

  return (
    <PageContent title="Entra-brugersynkronisering">
      {/* No h-full here: PageContent's own container is viewport-height with
          overflow-y-auto, and a full-height child would pin this page to that height
          and let Card's overflow-hidden clip everything below the fold. */}
      <div className="flex w-full flex-col gap-6 p-4 pb-10 xl:px-8">
        <Card className="shrink-0">
          <CardHeader>
            <CardTitle>Enterprise Application sync</CardTitle>
            <CardDescription>
              Synkroniserer eksisterende brugere og roller fra kundens
              Enterprise Application (timevis). Nye brugere oprettes stadig ved
              første login. Opt-in — a-kasser uden admin consent påvirkes ikke.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {statusQuery.isLoading && <p>Henter status…</p>}
            {statusQuery.isError && (
              <p className="text-destructive">Kunne ikke hente sync-status.</p>
            )}
            {status && (
              <dl className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">Aktiveret</dt>
                  <dd>{status.enabled ? "Ja" : "Nej"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Live sync</dt>
                  <dd>{status.liveEnabled ? "Ja" : "Nej"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Consent</dt>
                  <dd>{formatDate(status.consentedAt)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Sidste dry-run</dt>
                  <dd>{formatDate(status.dryRunCompletedAt)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Sidste kørsel</dt>
                  <dd>{formatDate(status.lastRunAt)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Sidste fejl</dt>
                  <dd>{status.lastError ?? "—"}</dd>
                </div>
              </dl>
            )}

            {status?.liveRunStartedAt && (
              <p className="text-warning">
                Live-kørsel i gang — startet{" "}
                {formatDate(status.liveRunStartedAt)}
                {status.liveRunInitiatedBy
                  ? ` af ${status.liveRunInitiatedBy}`
                  : ""}
                . Siden opdaterer automatisk, når den er færdig.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                id="entra-sync-consent-btn"
                variant="outline"
                disabled={consentUrlMutation.isPending || !tenantId}
                onClick={() => consentUrlMutation.mutate()}
              >
                Åbn admin consent
              </Button>
              <Button
                id="entra-sync-dry-run-btn"
                variant="secondary"
                disabled={
                  dryRunMutation.isPending || !tenantId || !status?.enabled
                }
                onClick={() => dryRunMutation.mutate()}
              >
                Kør dry-run
              </Button>
              <Button
                id="entra-sync-approve-live-btn"
                disabled={
                  approveLiveMutation.isPending ||
                  !tenantId ||
                  !status?.enabled ||
                  !status.dryRunCompletedAt ||
                  status.liveEnabled
                }
                onClick={handleApproveLive}
              >
                Godkend live sync
              </Button>
              <Button
                id="entra-sync-disable-live-btn"
                variant="outline"
                disabled={
                  disableLiveMutation.isPending ||
                  !tenantId ||
                  !status?.liveEnabled
                }
                onClick={handleDisableLive}
              >
                Deaktiver live sync
              </Button>
              <Button
                id="entra-sync-run-live-btn"
                variant="secondary"
                disabled={
                  runLiveMutation.isPending ||
                  !tenantId ||
                  !status?.liveEnabled ||
                  liveRunInProgress
                }
                onClick={handleRunLive}
              >
                Kør live sync nu
              </Button>
              {dryRunReport && dryRunReport.usersRemoved > 0 && (
                <Button
                  id="entra-sync-override-guard-btn"
                  variant="destructive"
                  disabled={
                    runLiveMutation.isPending ||
                    !tenantId ||
                    !status?.liveEnabled ||
                    liveRunInProgress
                  }
                  onClick={handleOverrideGuard}
                >
                  Tilsidesæt værn og fjern {dryRunReport.usersRemoved}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {dryRunReport && (
          <SyncResultReport
            title="Dry-run resultat"
            subtitle={
              dryRunResult
                ? "Forhåndsvisning af næste kørsel — ingen brugere er ændret."
                : "Senest gemte delta — kan stamme fra den timevise kørsel. Kør dry-run for et friskt overblik."
            }
            result={dryRunReport}
          />
        )}

        {lastLiveReport && (
          <SyncResultReport
            title="Seneste live-kørsel"
            subtitle="Resultatet af den seneste kørsel, du selv startede — ændringerne er anvendt."
            result={lastLiveReport}
          />
        )}
      </div>
    </PageContent>
  );
};

export default EntraDirectorySyncView;
